import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  addOrder,
  updateOrder,
  deleteOrder,
  deleteAllOrders,
  recalculateOrderFulfillment
} from '../../store/slices/ordersSlice';
import { OrderCard } from './components/OrderCard';
import { OrderSummary } from './components/OrderSummary';
import CreateOrderModal from './CreateOrderModal';
import GenerateOrdersModal from './GenerateOrdersModal';
import ReviewQueueModal from './ReviewQueueModal';
import { Recipe } from '../../types/recipe';
import { Order } from '../../types/order';
import { CalendarEvent } from '../../types/calendar';
import { emailScheduler } from '../../services/emailSchedulerService';

export const Orders: React.FC = () => {
  const dispatch = useAppDispatch();
  const orders = useAppSelector(state => state.orders.orders);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const completedProducts = useAppSelector(state => state.todo.completedProducts);
  const calendarEvents = useAppSelector(state => state.calendar.events);
  const reviewQueueItems = useAppSelector(state => state.reviewQueue.items);
  const [highlightedOrderId, setHighlightedOrderId] = useState<string | null>(null);
  const [expandedPendingOrderId, setExpandedPendingOrderId] = useState<string | null>(null);
  const [expandedInProgressOrderId, setExpandedInProgressOrderId] = useState<string | null>(null);
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [inProgressExpanded, setInProgressExpanded] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isReviewQueueModalOpen, setIsReviewQueueModalOpen] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const orderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Recalculate order fulfillment when component mounts or inventory changes
  useEffect(() => {
    dispatch(recalculateOrderFulfillment(completedProducts));
  }, [dispatch, completedProducts]);

  // Only use fillings for order items (not components)
  const recipeOptions = fillings.map((r: Recipe) => ({
    id: r.id,
    name: r.name
  }));

  const handleCreateOrder = () => {
    setIsCreateModalOpen(true);
  };

  const handleGenerateOrders = () => {
    setIsGenerateModalOpen(true);
  };

  const handleUpdateOrder = (updatedOrder: any) => {
    dispatch(updateOrder(updatedOrder));
  };

  const handleDeleteOrder = (id: string) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      dispatch(deleteOrder(id));
    }
  };

  const handleDeleteAllOrders = () => {
    if (window.confirm('Are you sure? This will permanently delete ALL orders.')) {
      dispatch(deleteAllOrders());
    }
  };

  const handleCheckEmail = async () => {
    setIsCheckingEmail(true);
    try {
      await emailScheduler.runManualCheck();
    } catch (error) {
      console.error('Email check failed:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Handle order card expansion with accordion behavior
  const handleOrderToggle = (orderId: string, status: string) => {
    if (status === 'Pending') {
      // Toggle: if clicking the same order, collapse it; otherwise, expand the new one
      setExpandedPendingOrderId(prev => (prev === orderId ? null : orderId));
    } else if (status === 'In Progress') {
      // Toggle: if clicking the same order, collapse it; otherwise, expand the new one
      setExpandedInProgressOrderId(prev => (prev === orderId ? null : orderId));
    }
  };

  // Handle navigation from flavor summary to specific order
  const handleNavigateToOrder = (orderId: string) => {
    // Find which section this order belongs to and expand it
    const order = orders.find(o => o.id === orderId);
    if (order) {
      if (order.status === 'Pending') {
        setPendingExpanded(true);
        setExpandedPendingOrderId(orderId);
      } else if (order.status === 'In Progress') {
        setInProgressExpanded(true);
        setExpandedInProgressOrderId(orderId);
      }
    }

    // Highlight the order
    setHighlightedOrderId(orderId);

    // Scroll to the order (increased delay to account for section expansion)
    setTimeout(() => {
      const orderElement = orderRefs.current[orderId];
      if (orderElement) {
        orderElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 200); // Increased from 100ms to 200ms

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedOrderId(null);
    }, 2500);
  };

  // Filter out orders that have a completed delivery task
  const activeOrders = orders.filter((order: Order) => {
    // Find delivery event for this order
    const deliveryEvent = calendarEvents.find((event: CalendarEvent) => event.orderId === order.id);
    // Show order only if it has no delivery event OR delivery event is not completed
    return !deliveryEvent || !deliveryEvent.completed;
  });

  // Sort orders by date (newest first) and status
  const sortedOrders = [...activeOrders].sort((a, b) => {
    // First sort by status (Pending > In Progress > Completed)
    const statusOrder: Record<string, number> = {
      Pending: 0,
      'In Progress': 1,
      Completed: 2
    };

    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;

    // Then by date (oldest pending orders first)
    return new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime();
  });

  // Split orders by status for collapsible sections
  const pendingOrders = useMemo(
    () => sortedOrders.filter((order: Order) => order.status === 'Pending'),
    [sortedOrders]
  );

  const inProgressOrders = useMemo(
    () => sortedOrders.filter((order: Order) => order.status === 'In Progress'),
    [sortedOrders]
  );

  // Render section header for collapsible order groups
  const renderSectionHeader = (
    title: string,
    count: number,
    isExpanded: boolean,
    onClick: () => void,
    color: string
  ) => (
    <div
      onClick={onClick}
      style={{
        padding: '16px 20px',
        backgroundColor: '#f9f9f9',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        marginBottom: isExpanded ? '16px' : '8px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.2s ease-in-out',
        userSelect: 'none'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = '#f9f9f9';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span
          style={{
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
            display: 'inline-block',
            fontSize: '1rem',
            color: '#666'
          }}
        >
          ▶
        </span>
        <h2 style={{ fontSize: '1.3rem', margin: 0, color: '#333', fontWeight: '600' }}>
          {title} ({count})
        </h2>
      </div>
      <div
        style={{
          padding: '6px 12px',
          backgroundColor: color,
          color: 'white',
          borderRadius: '4px',
          fontSize: '0.85rem',
          fontWeight: 'bold'
        }}
      >
        {count} {count === 1 ? 'order' : 'orders'}
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <header
          style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Orders</h1>
              <p style={{ color: '#666', margin: 0 }}>
                {activeOrders.length} active order{activeOrders.length !== 1 ? 's' : ''}
              </p>
            </div>

            {reviewQueueItems.length > 0 && (
              <button
                onClick={() => setIsReviewQueueModalOpen(true)}
                style={{
                  position: 'relative',
                  padding: '10px 16px',
                  backgroundColor: '#FF6B35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(255, 107, 53, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#E85D2E';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 107, 53, 0.4)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = '#FF6B35';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 107, 53, 0.3)';
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>⚠</span>
                Review Queue
                <span
                  style={{
                    backgroundColor: 'white',
                    color: '#FF6B35',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    minWidth: '24px',
                    textAlign: 'center'
                  }}
                >
                  {reviewQueueItems.length}
                </span>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleCheckEmail}
              disabled={isCheckingEmail}
              style={{
                padding: '12px 24px',
                backgroundColor: isCheckingEmail ? '#9E9E9E' : '#9C27B0',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isCheckingEmail ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isCheckingEmail ? 0.7 : 1
              }}
            >
              {isCheckingEmail ? (
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  Checking...
                </>
              ) : (
                <>📧 Check Email</>
              )}
            </button>

            <button
              onClick={handleDeleteAllOrders}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              Delete All Orders
            </button>

            <button
              onClick={handleGenerateOrders}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              Generate Orders
            </button>

            <button
              onClick={handleCreateOrder}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              + Create New Order
            </button>
          </div>
        </header>

        {/* Status Summary */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}
        >
          <div
            style={{
              padding: '16px',
              backgroundColor: '#FFF3E0',
              borderLeft: '4px solid #FF9800',
              borderRadius: '4px'
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', color: '#FF9800' }}>Pending</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {activeOrders.filter((o: Order) => o.status === 'Pending').length}
            </p>
          </div>

          <div
            style={{
              padding: '16px',
              backgroundColor: '#E3F2FD',
              borderLeft: '4px solid #2196F3',
              borderRadius: '4px'
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', color: '#2196F3' }}>In Progress</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {activeOrders.filter((o: Order) => o.status === 'In Progress').length}
            </p>
          </div>
        </div>

        {/* Order Summary - Units by Flavor */}
        <OrderSummary orders={activeOrders} onNavigateToOrder={handleNavigateToOrder} />

        {/* Orders List - Organized by Status */}
        <div style={{ marginBottom: '24px' }}>
          {/* Pending Orders Section */}
          <div style={{ marginBottom: '16px' }}>
            {renderSectionHeader(
              'Pending Orders',
              pendingOrders.length,
              pendingExpanded,
              () => setPendingExpanded(!pendingExpanded),
              '#FF9800'
            )}

            {pendingExpanded && (
              <div style={{ animation: 'fadeIn 0.2s ease-in-out' }}>
                {pendingOrders.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#999',
                      marginBottom: '8px'
                    }}
                  >
                    <p style={{ fontSize: '1rem', margin: 0 }}>No pending orders</p>
                  </div>
                ) : (
                  pendingOrders.map(order => (
                    <OrderCard
                      key={order.id}
                      ref={el => {
                        orderRefs.current[order.id] = el;
                      }}
                      order={order}
                      recipes={recipeOptions}
                      onUpdate={handleUpdateOrder}
                      onDelete={handleDeleteOrder}
                      isHighlighted={highlightedOrderId === order.id}
                      forceExpanded={expandedPendingOrderId === order.id}
                      onToggle={() => handleOrderToggle(order.id, order.status)}
                    />
                  ))
                )}
              </div>
            )}
          </div>

          {/* In Progress Orders Section */}
          <div>
            {renderSectionHeader(
              'In Progress Orders',
              inProgressOrders.length,
              inProgressExpanded,
              () => setInProgressExpanded(!inProgressExpanded),
              '#2196F3'
            )}

            {inProgressExpanded && (
              <div style={{ animation: 'fadeIn 0.2s ease-in-out' }}>
                {inProgressOrders.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#999'
                    }}
                  >
                    <p style={{ fontSize: '1rem', margin: 0 }}>No in progress orders</p>
                  </div>
                ) : (
                  inProgressOrders.map(order => (
                    <OrderCard
                      key={order.id}
                      ref={el => {
                        orderRefs.current[order.id] = el;
                      }}
                      order={order}
                      recipes={recipeOptions}
                      onUpdate={handleUpdateOrder}
                      onDelete={handleDeleteOrder}
                      isHighlighted={highlightedOrderId === order.id}
                      forceExpanded={expandedInProgressOrderId === order.id}
                      onToggle={() => handleOrderToggle(order.id, order.status)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <CreateOrderModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          availableRecipes={recipeOptions}
        />

        <GenerateOrdersModal
          isOpen={isGenerateModalOpen}
          onClose={() => setIsGenerateModalOpen(false)}
          availableRecipes={recipeOptions}
        />

        <ReviewQueueModal
          isOpen={isReviewQueueModalOpen}
          onClose={() => setIsReviewQueueModalOpen(false)}
          availableRecipes={recipeOptions}
        />
      </div>
    </>
  );
};
